// game/boss.ts — the Boss base class (ARCHITECTURE §4).
// Phases, the rolling burst cap, and the telegraph helper live here so that every
// concrete boss inherits the same fairness guarantees: no attack exists that did
// not announce itself, and no build can delete a phase transition.
//
// No concrete boss ships in Phase 4. content/bosses/bossN.ts extends this.

import type { BossLike, Draw, RunCtx, Vec } from '../types'
import { TUNING } from '../data/tuning'

export interface Telegraph {
  kind: string
  /** seconds remaining */
  t: number
  duration: number
  done: boolean
  /** 0..1 progress, for pose interpolation */
  readonly k: number
}

class TelegraphImpl implements Telegraph {
  kind = ''
  t = 0
  duration = 0.001
  done = false
  get k(): number {
    return 1 - Math.max(0, Math.min(1, this.t / this.duration))
  }
}

interface BurstSample {
  t: number
  n: number
}

export abstract class Boss implements BossLike {
  abstract readonly id: string
  hp = 100
  maxHp = 100
  phase = 1
  alive = true
  pos: Vec = { x: 0, y: 0 }
  radius = 40

  /** Set by the zone runner each frame so telegraph()/sfx() can reach the run. */
  protected ctx: RunCtx | null = null

  private telegraphs: TelegraphImpl[] = []
  private burst: BurstSample[] = []
  private burstHead = 0
  private simTime = 0

  constructor(maxHp: number) {
    this.maxHp = maxHp
    this.hp = maxHp
    for (let i = 0; i < 8; i++) this.telegraphs.push(new TelegraphImpl())
    for (let i = 0; i < 32; i++) this.burst.push({ t: -999, n: 0 })
  }

  abstract update(ctx: RunCtx, dt: number): void
  abstract render(d: Draw): void

  /** Called when the phase index changes. Override to re-arm behaviour. */
  protected onPhase(_phase: number): void {}

  /**
   * Phases are MONOTONIC: a healing boss (the Hoard's gulps, the Sovereign's
   * husk-heart) can raise its HP back across a threshold, and re-firing an
   * earlier phase — or re-firing the current one on the way back down — re-arms
   * live attack state mid-swing (Phase 7 found boss5's sweep re-entry rendering
   * a negative arc radius for 3s and holding its damage band open). A fight
   * only ever moves forward.
   */
  protected setPhase(n: number): void {
    if (n <= this.phase) return
    this.phase = n
    this.onPhase(n)
  }

  /**
   * Damage with a rolling burst cap: at most burstCapFraction of maxHp can be
   * deleted inside burstCapWindowSeconds. Big builds still win faster; they
   * cannot skip a phase before its telegraphed transition has been seen.
   */
  takeDamage(n: number): void {
    if (!this.alive || n <= 0) return
    const win = TUNING.bosses.burstCapWindowSeconds
    const cap = this.maxHp * TUNING.bosses.burstCapFraction
    let recent = 0
    for (let i = 0; i < this.burst.length; i++) {
      const s = this.burst[i]
      if (this.simTime - s.t <= win) recent += s.n
    }
    const allowed = Math.max(0, cap - recent)
    const applied = Math.min(n, allowed)
    if (applied <= 0) return
    const slot = this.burst[this.burstHead]
    slot.t = this.simTime
    slot.n = applied
    this.burstHead = (this.burstHead + 1) % this.burst.length
    this.hp = Math.max(0, this.hp - applied)
    if (this.hp <= 0) this.alive = false
  }

  /**
   * ALL boss attacks go through this. Windup flash + sound + a handle the
   * subclass polls; when `done` flips the attack fires.
   */
  protected telegraph(kind: string, seconds: number): Telegraph {
    let slot = this.telegraphs.find((t) => t.done || t.t <= 0)
    if (!slot) {
      slot = new TelegraphImpl()
      this.telegraphs.push(slot)
    }
    slot.kind = kind
    slot.duration = Math.max(0.05, seconds)
    slot.t = slot.duration
    slot.done = false
    this.ctx?.sfx('telegraph')
    return slot
  }

  /** Subclasses call this first in update(). */
  protected tick(ctx: RunCtx, dt: number): void {
    this.ctx = ctx
    this.simTime += dt
    for (let i = 0; i < this.telegraphs.length; i++) {
      const t = this.telegraphs[i]
      if (t.done || t.t <= 0) continue
      t.t -= dt
      if (t.t <= 0) {
        t.t = 0
        t.done = true
      }
    }
  }

  /** Default windup rendering: a swelling ring the player can read at a glance. */
  protected renderTelegraphs(d: Draw): void {
    for (let i = 0; i < this.telegraphs.length; i++) {
      const t = this.telegraphs[i]
      if (t.done || t.t <= 0) continue
      const k = t.k
      d.circle(this.pos, this.radius + 10 + k * 14, '#ffd166', 0.12 + 0.25 * k)
    }
  }

  get healthFraction(): number {
    return this.maxHp > 0 ? this.hp / this.maxHp : 0
  }
}

// ---------------------------------------------------------------- registry

export type BossFactory = (arenaCenter: Vec, hpScale: number) => Boss

const BOSSES = new Map<string, BossFactory>()

export function registerBoss(id: string, factory: BossFactory): void {
  BOSSES.set(id, factory)
}

export function makeBoss(id: string, arenaCenter: Vec, hpScale = 1): Boss | null {
  const f = BOSSES.get(id)
  return f ? f(arenaCenter, hpScale) : null
}

export function bossIds(): string[] {
  return [...BOSSES.keys()]
}
